/*
* Regional Component List Edit - allows editing of the selected regional component list
*/

import '@progress/kendo-ui';
import '../inventory/detail-form/detail-form.css';

import { Grid } from '@progress/kendo-grid-react-wrapper';
import * as _ from 'lodash';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, DropdownButton, MenuItem, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import Select from 'react-select';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';
import * as MutateActions from '../actions/mutate';
import Auth from '../auth/auth';
import * as AppComp from '../common/app-component-base';
import { AppDataConstants } from '../common/app-data-constants';
import * as AppUtilities from '../common/apputilities';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { FormUtilities } from '../common/formutilities';
import { GridUtilities } from '../common/grid/gridutilities';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import * as LK from '../config/apilookupkeys';
import * as DetailFormCommon from '../inventory/detail-form/common';
import {
    ItemBulkUpdateOperations,
    ItemBulkUpdateFields,
} from '../inventory';
import * as ReduxQueries from '../queries';

export interface IRegionalComponentListEditProps extends AppComp.IAppComponentProps {
    forceRequest?: any;
    navigateTo?: any;
    regionalComponentListId?: any;
    regionalComponentList?: any;
    updateRegionalComponentListObj?: any;
    updateRegionalComponentListItem?: any;
    newRegionalComponentList?: any;
    deleteRegionalComponentListItemObj?: any;
    newRegionalComponentListItem?: any;
    renameRegionalComponentListObj?: any;
    deleteRegionalComponentListObj?: any;
    newRegionalComponentListSubgroup?: any;
    updateRegionalComponentListSubgroup?: any;
    deleteRegionalComponentListSubgroup?: any;
    duplicateRegionalComponentListSubgroup?: any;
    handleLinkNavigation?: any;
    lookups?: any;
}

export interface IRegionalComponentListEditState extends AppComp.IAppComponentState {
    avgCostAdjustmentValue: number;
    bulkUpdateItemsOperation: string;
    bulkUpdateItemsValue: any;
    editData?: any;
    formChanges?: any;
    selectedFilters: any;
    selectedKeys?: any;
    showModalAddSubgroup: boolean;
    showModalAddInventoryTemplate?: boolean;
    showModalApplyInventoryTemplate?: boolean;
    showModalBulkUpdateItems: boolean;
    showModalDeleteItems: boolean;
    showModalDeleteSubgroup: boolean;
    showModalDuplicateSubgroup: boolean;
    showModalMoveItems: boolean;
    showModalPicturesQuestionsResetItems: boolean;
    showModalRemoveTemplateItem?: boolean;
    showModalRenameSubgroup: boolean;
    showModalRenameTemplate?: boolean;
    showModalSaveChanges: boolean;
    showModalDeleteTemplate?: boolean;
    showModalAddComponent?: boolean;
    addComponentSelectedId?: string;
}

export const LinkButton = ({ className, linkTo, children, ...props }) => (
    <Link className={`btn btn-sm ${className}`} to={linkTo} {...props}>{...children}</Link>
);

export const DefaultGridPageTake: number = 50;
export const DefaultFollowUpAddInterval = 7;
export const DefaultFollowUpAddPeriod = 'days';

class RegionalComponentListEdit extends AppComp.AppComponent<IRegionalComponentListEditProps, IRegionalComponentListEditState> {
    public originalFormDataCache = {};
    public originalExtendedFormDataCache: Array<any> = [];
    /** Cache for regionOptions so dropdown gets stable reference when lookups haven't changed */
    public _cachedRegionOptions: { regions: any; options: Array<{ value: string; text: string }> } | null = null;
    public readonly virtualizedDropdownPageSize: number = 20;
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public anyOptionText: string = this.appTextFilterOptionAny();
    public DefaultSubgroupFilter = '#DEFAULT#';
    public readonly GridEditField: string = 'editField';
    public readonly GridNewRecordField: string = 'newRecordField';
    public readonly GridHideRecordField: string = 'hideRecordField';
    public kendoGridWidgetRef: any;

    public gridOptions = GridUtilities.kendoGridOptions(
        [
            GridUtilities.kendoGridColumnDefinition('', '', 30, 100, undefined, { selectable: true }),
            GridUtilities.kendoGridColumnDefinition('componentId', this.appTextGridColumnHeader('id'), 50, 110),
            GridUtilities.kendoGridColumnDefinition('componentName', this.appTextGridColumnHeader('component'), 220, 120),
            GridUtilities.kendoGridColumnDefinition('categoryName', this.appTextGridColumnHeader('category'), 150, 130),
            GridUtilities.kendoGridColumnDefinition('quantity', this.appTextGridColumnHeader('qty'), 150, 140),
            GridUtilities.kendoGridColumnDefinition(
                'usefulLife',
                this.appTextGridColumnHeader('ul'),
                50,
                150,
                undefined,
                undefined,
                'usefulLife_sort'
            ),
            GridUtilities.kendoGridColumnDefinition(
                'remainingUsefulLife',
                this.appTextGridColumnHeader('rl'),
                50,
                160,
                undefined,
                undefined,
                'remainingUsefulLife_sort'
            ),
            GridUtilities.kendoGridColumnDefinition(
                'avgCost',
                this.appTextGridColumnHeader('avg-cost'),
                100,
                170,
                undefined,
                {
                    format: '{0:c}',
                },
                'avgCost_sort'
            ),
            GridUtilities.kendoGridColumnDefinition('viewInventoryDetailLink', ' ', 100, 180, GridUtilities.kendoGridLinkColumnTemplate(
                'viewInventoryDetailLink', 'viewInventoryDetailLinkHref', this.appText('view-inventory-detail-link-title')
            )),
        ],
        this.appText('no-results'),
        {
            toolbar: ['excel'],
            excel: {
                allPages: true,
            },
            persistSelection: true,
            widgetRef: (w) => { this.kendoGridWidgetRef = w; },
            change: (e) => {
                const selectedKeys = e.sender.selectedKeyNames();
                this.handleGridRowCheckbox(selectedKeys);
            },
        }
    );

    public constructor(props: IRegionalComponentListEditProps) {
        super(props, 'app.regional-component-list-edit.');

        const selectedFiltersSubgroup = Formatters.getStr(props, 'filterBySubgroup.subgroup', this.DefaultSubgroupFilter);

        this.state = {
            avgCostAdjustmentValue: 0,
            bulkUpdateItemsOperation: '',
            bulkUpdateItemsValue: undefined,
            editData: {},
            formChanges: {},
            selectedFilters: { subgroup: selectedFiltersSubgroup },
            selectedKeys: [],
            showModalAddSubgroup: false,
            showModalAddInventoryTemplate: false,
            showModalApplyInventoryTemplate: false,
            showModalBulkUpdateItems: false,
            showModalDeleteItems: false,
            showModalDeleteSubgroup: false,
            showModalDuplicateSubgroup: false,
            showModalMoveItems: false,
            showModalPicturesQuestionsResetItems: false,
            showModalRemoveTemplateItem: false,
            showModalRenameSubgroup: false,
            showModalRenameTemplate: false,
            showModalSaveChanges: false,
            showModalDeleteTemplate: false,
            showModalAddComponent: false,
            addComponentSelectedId: '',
        };
    }

    /** Add Component: create new list item via API, then navigate to detail page with the new item id (same pattern as inventory Add Component). */
    public handleAddComponentButton = (formData: any) => {
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        if (!regionalComponentListId || regionalComponentListId === 'new') { return; }
        const token = Formatters.getStr(this.props, 'authsession.access_token', '');
        if (!token) { return; }
        const changeData = { regionalComponentListId };
        this.props.newRegionalComponentListItem(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                const newItemId =
                    Formatters.getStr(response.body, 'regionalComponentListInventoryItem.document.inventoryId') ||
                    Formatters.getStr(response.body, 'regionalComponentListInventoryItem.document._id') ||
                    Formatters.getStr(response.body, 'newItemId') ||
                    Formatters.getStr(response.body, 'regionalComponentListInventoryItem._id');
                if (newItemId) {
                    // Refetch the list so Redux has the new item, then navigate to detail
                    const nav = () => this.props.navigateTo(NavigationLinks.regionalComponentListDetail(regionalComponentListId, newItemId));
                    if (this.props.forceRequest) {
                        this.props.forceRequest();
                        setTimeout(nav, 400);
                    } else {
                        nav();
                    }
                }
            }
        });
    }

    public handleCloseModalAddComponent = () => {
        this.setState({ showModalAddComponent: false, addComponentSelectedId: '' });
    }

    public handleConfirmAddComponent = () => {
        const { lookups } = this.props;
        const templateSubgroups: any[] = [];
        const selectedId = this.state.addComponentSelectedId;
        if (!selectedId) { return; }
        const components = _.get(lookups, LK.LookupKeys.Components, []) as any[];
        const comp = _.find(components, (c) => String(Formatters.getStr(c, 'componentId') || Formatters.getStr(c, '_id')) === String(selectedId));
        if (!comp) { return; }
        const subgroup = Formatters.getStr(this.state, 'selectedFilters.subgroup', '');
        const sg = (subgroup !== '' && subgroup !== this.DefaultSubgroupFilter) ? subgroup : (_.get(templateSubgroups, '[0].groupCode', 'A') || 'A');
        const baseInv = this.extendInventoryDataForGrid([], templateSubgroups);
        const currentList = _.get(_.merge({}, { inventory: baseInv }, this.state.formChanges), 'inventory', baseInv);
        const compId = Formatters.getInt(comp, 'componentId') || Formatters.getInt(comp, '_id');
        const newItem: any = {
            _id: `new-${Date.now()}`,
            componentId: compId,
            componentName: Formatters.getStr(comp, 'name', ''),
            categoryName: Formatters.getStr(comp, 'category', ''),
            subgroup: sg,
            quantity: 1,
            usefulLife: 0,
            remainingUsefulLife: 0,
            complete: false,
            reviewed: false,
            component: { componentId: compId, name: Formatters.getStr(comp, 'name'), category: Formatters.getStr(comp, 'category') },
        };
        const extendedNew = this.extendInventoryDataForGrid([newItem], templateSubgroups)[0] || newItem;
        const newList = [...currentList, extendedNew];
        this.setState({
            formChanges: _.merge({}, this.state.formChanges, { inventory: newList }),
            showModalAddComponent: false,
            addComponentSelectedId: '',
        });
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();
        const shouldReset = (resetFormData && _.size(this.state.formChanges)) ? true : false;

        if (shouldReset) {
            this.setState({ formChanges: {} });
        }
    }

    public extendTemplateData(template: any) {
        if (!template) { return; }
        let result = _.cloneDeep(template);
        const subgroups = _.get(template, 'templateSubgroups', []) || [];
        if (AppUtilities.isArrayWithLength(subgroups)) {
            result.inventorySubgroups = [...subgroups];
            result.inventorySubgroups.push({ groupCode: '', title: '', label: this.appText('filter-option-any') });
            result.inventorySubgroups = result.inventorySubgroups.map((c) => {
                if (!c.label) { c.label = Formatters.composeInventorySubgroupLabel(c, true); }
                return c;
            });
        }
        return result;
    }

    public transformNotes(records: Array<any>) {
        if (!_.size(records)) { return []; }
        return _.orderBy(_.filter(_.cloneDeep(records), (o) => Formatters.getStr(o, 'notes') !== ''), ['created'], ['desc']);
    }

    public transformInventoryRUL(records: Array<any>, adjustRULYears: number = 0, hideUnfunded: boolean = false, clearPicturesQuestions: boolean = false, restorePrevPictures: boolean = false) {
        if (!records) { return []; }
        let result = _.cloneDeep(records);
        if (hideUnfunded) { result = _.filter(result, (o) => !Formatters.getBool(o, 'unfunded')); }
        const selectedKeys = _.get(this.state, 'selectedKeys', []);
        const hasSelectedKeys = _.size(selectedKeys) > 0;
        const adjustAmount = Number(adjustRULYears);
        return result.map((r) => {
            const record = _.cloneDeep(r);
            const recordId = Formatters.getStr(record, '_id');
            const isSelected = !hasSelectedKeys || _.includes(selectedKeys, recordId);
            if (isSelected && adjustAmount) {
                let newRUL = Number(Formatters.getInt(record, 'remainingUsefulLife', 0)) + adjustAmount;
                if ((Formatters.getStr(record, 'usefulLife') === AppDataConstants.UsefulLifeOptions.NotApplicable) || (Formatters.getStr(record, 'usefulLife') === '') || Formatters.getBool(record, 'unfunded')) {
                    newRUL = newRUL < 0 ? 0 : newRUL;
                }
                if (Formatters.getInt(record, 'remainingUsefulLife', 0) !== newRUL) {
                    _.set(record, 'remainingUsefulLife', newRUL);
                    _.set(record, 'remainingUsefulLife_sort', newRUL);
                    _.set(record, 'remainingUsefulLifeChanged', true);
                    _.set(record, 'complete', false);
                    _.set(record, 'complete_yesNo', this.appText('complete-no'));
                    _.set(record, 'reviewed', false);
                    _.set(record, 'reviewed_yesNo', this.appText('reviewed-no'));
                    _.set(record, 'adjustRULYears', adjustAmount);
                }
            }
            if (isSelected && clearPicturesQuestions) { _.set(record, 'clearPicturesQuestions', true); }
            if (isSelected && restorePrevPictures) { _.set(record, 'restorePrevPictures', true); }
            return record;
        });
    }

    // Handlers
    public handleBackToManager() {
        this.props.navigateTo(NavigationLinks.regionalComponentListManager());
    }

    public handleShowModalDeleteTemplate(e: any, templateId: string) {
        this.setState({ showModalDeleteTemplate: true, editData: _.cloneDeep(templateId) });
    }

    public handleShowModalRenameTemplate(e: any, templateId: string) {
        this.setState({ showModalRenameTemplate: true, editData: _.cloneDeep(templateId) });
    }

    public handleShowModalRemoveTemplateItem(e: any, selectedDataItem: any) {
        this.setState({ showModalRemoveTemplateItem: true, editData: _.cloneDeep(selectedDataItem) });
    }

    public handleCloseModalRemoveTemplateItem() {
        this.setState({ showModalRemoveTemplateItem: false, editData: {} });
    }

    public handleConfirmRemoveTemplateItem(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const curEdit = _.get(this.state, 'editData', {});
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        const regionalComponentListInventoryItemId = Formatters.getStr(curEdit, '_id');

        const changeData = _.merge(
            {},
            { obj: _.pick(curEdit, ['_id']) },
            { regionalComponentListId: regionalComponentListId },
            { regionalComponentListInventoryItemId: regionalComponentListInventoryItemId },
        );

        this.props.deleteRegionalComponentListItemObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    showModalRemoveTemplateItem: false,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCloseModalRenameTemplate() {
        this.setState({ showModalRenameTemplate: false, editData: {} });
    }

    public handleConfirmRenameTemplate(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const curEdit = _.get(this.state, 'editData', {});
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        // const regionalComponentListInventoryItemId = Formatters.getStr(curEdit, '_id');
        const requestedNewName = Formatters.getStr(curEdit, 'newName');
        const requestedNewDescription = Formatters.getStr(curEdit, 'newDescription');

        const changeData = _.merge(
            {},
            { regionalComponentListId: regionalComponentListId },
            { requestedNewName: requestedNewName, requestedNewDescription: requestedNewDescription },
        );

        this.props.renameRegionalComponentListObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    showModalRenameTemplate: false,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCloseModalDeleteTemplate() {
        this.setState({ showModalDeleteTemplate: false, editData: {} });
    }

    public handleConfirmDeleteTemplate(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');

        const changeData = _.merge(
            {},
            { regionalComponentListId: regionalComponentListId },
        );

        this.props.deleteRegionalComponentListObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.props.navigateTo(NavigationLinks.regionalComponentListManager());
                /* this.setState({
                    editData: {},
                    showModalDeleteTemplate: false,
                });
                this.props.forceRequest(); */
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        let fieldName = '';
        let newValue;

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', ''); // reportNotesDelete
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }
        if (!fieldName) { return; }

        // Guard: avoid re-firing loop when Kendo re-sends onChange for same value (e.g. controlled value update)
        if (fieldName === 'regionId') {
            let currentRegionId = Formatters.getStr(this.state.formChanges, 'regionId');
            if (currentRegionId === undefined || currentRegionId === '') {
                const list = this.props.regionalComponentList || {};
                const isNewRegion = Formatters.getStr(this.props, 'regionalComponentListId') === 'new';
                currentRegionId = isNewRegion ? '' : (
                    Formatters.getStr(list, 'region.regionId') ||
                    Formatters.getStr(list, 'region._id') ||
                    Formatters.getStr(list, 'regionId') ||
                    ''
                );
            }
            if (String(newValue) === String(currentRegionId)) { return; }
        }

        let formChanges = _.cloneDeep(this.state.formChanges);

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        if (_.isArray(origVal)) {
            if (_.includes(origVal, newValue)) {
                applyChange = false;
            } else {
                const formChangesArr = _.get(formChanges, fieldName, []);

                if (_.includes(formChangesArr, newValue)) {
                    applyChange = false;
                } else {
                    formChangesArr.push(newValue);
                    _.set(formChanges, fieldName, _.cloneDeep(formChangesArr));
                    applyChange = true;
                    // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChanges, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChanges, fieldName)) {
                    _.unset(formChanges, fieldName);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            this.setState({
                formChanges: formChanges
            });
        }
    }

    // BUTTON HANDLERS
    public handleEnterDetails(d: any) {
        // console.log('handleEnterDetails', { d });
        // this.props.navigateTo(NavigationLinks.lookupTableEditChildObj(tableName, childObjId));
    }

    public handleSave(d: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');

        const changeData = _.merge(
            {},
            { obj: _.pick(d, ['_id', 'componentName', 'quantity', 'usefulLife', 'remainingUsefulLife']) },
            { regionalComponentListId: regionalComponentListId },
        );

        this.props.updateRegionalComponentListObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCreate(d: any) {
        // console.log(`handleCreate`, { d });
    }

    public handleCancel(d: any) {
        this.setState({
            editData: {},
        });
    }

    public handleGridItemChange(e: any) {
        let prev = _.cloneDeep(_.get(this.state, 'editData', {}));

        let n = Formatters.getStr(e, 'field');
        let v = _.get(e, 'value');

        // console.log(`handleGridItemChange n ${n} v ${v}`, { e, prev });

        if (!n) { return; }

        _.set(prev, n, v);

        this.setState({ editData: prev });
    }

    public handleShowDetails(d: any) {
        // console.log(`handleShowDetails`, { d });
        return false;
    }

    public handleShowDelete(d: any) {
        // console.log(`handleShowDelete`, { d });
        return true;
    }

    // Helpers
    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;

        // Props values
        const pa = Formatters.getInt(this.props, 'regionalComponentListId');

        // Cached values
        const ca = Formatters.getInt(this.originalFormDataCache, 'regionalComponentListId');

        // Determine if formdata state and cache needs reset due to changing list id
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    public setStateSelectedFilters(filter: any) {
        if (!filter) { return; }
        this.setState({ selectedFilters: Object.assign({}, this.state.selectedFilters, filter) });
    }

    public handleGridRowCheckbox(keys: any) {
        const nextSelectedKeys = _.cloneDeep(keys);
        const prevSelectedKeys = _.get(this.state, 'selectedKeys', []);
        if (!_.isEqual(nextSelectedKeys, prevSelectedKeys)) {
            this.setState({ selectedKeys: nextSelectedKeys });
        }
    }

    public handleSelectSubgroupFilter(e: any) {
        this.setStateSelectedFilters({ subgroup: e.value });
    }

    public handleShowModalDeleteItems(e: any) {
        this.setState({ showModalDeleteItems: true });
    }

    public handleCloseModalDeleteItems() {
        this.setState({ showModalDeleteItems: false });
    }

    public handleConfirmDeleteItemsButton(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        const selectedKeys = _.get(this.state, 'selectedKeys', []) as string[];

        const deletePromises = selectedKeys.map((id) =>
            this.props.deleteRegionalComponentListItemObj(
                { regionalComponentListId, regionalComponentListInventoryItemId: id },
                token,
                false
            )
        );
        Promise.all(deletePromises).then((responses) => {
            const allOk = responses.every((r: any) => r.status >= 200 && r.status < 300);
            if (allOk) {
                if (this.kendoGridWidgetRef) { this.kendoGridWidgetRef.clearSelection(); }
                this.setState({ showModalDeleteItems: false, selectedKeys: [] });
                this.props.forceRequest();
            }
        });
    }

    public handleShowModalMoveItems(e: any) {
        this.setState({ showModalMoveItems: true });
    }

    public handleCloseModalMoveItems() {
        this.setState({ showModalMoveItems: false, formChanges: _.merge({}, this.state.formChanges, { moveItems: { toGroupCode: '' } }) });
    }

    public handleConfirmMoveItemsButton(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        const toGroupCodeRaw = _.get(this.state, 'formChanges.moveItems.toGroupCode', '');
        const invMoveToSubgroup = _.isObject(toGroupCodeRaw) ? Formatters.getStr(toGroupCodeRaw, 'value') : String(toGroupCodeRaw || '').trim();
        const selectedKeys = _.get(this.state, 'selectedKeys', []) as string[];

        if (!invMoveToSubgroup || _.size(selectedKeys) === 0) { return; }

        if (this.kendoGridWidgetRef) { this.kendoGridWidgetRef.clearSelection(); }

        const movePromises = selectedKeys.map((id) =>
            this.props.updateRegionalComponentListItem(
                {
                    regionalComponentListId,
                    regionalComponentListInventoryItemId: id,
                    inventory: { _id: id },
                    subgroup: invMoveToSubgroup,
                },
                token,
                false
            )
        );
        Promise.all(movePromises).then((responses) => {
            const allOk = responses.every((r: any) => r.status >= 200 && r.status < 300);
            if (allOk) {
                if (this.kendoGridWidgetRef) { this.kendoGridWidgetRef.clearSelection(); }
                this.setState({ showModalMoveItems: false, formChanges: _.merge({}, this.state.formChanges, { moveItems: { toGroupCode: '' } }) });
                this.props.forceRequest();
            }
        });
    }

    public handleShowModalBulkUpdateItems(e: any, o: string = '', v?: any) {
        this.setState({ showModalBulkUpdateItems: true, bulkUpdateItemsOperation: o, bulkUpdateItemsValue: v });
    }

    public handleCloseModalBulkUpdateItems(resetState: boolean = false) {
        if (resetState) {
            this.setState({ showModalBulkUpdateItems: false, bulkUpdateItemsOperation: '', bulkUpdateItemsValue: undefined, formChanges: _.omit(this.state.formChanges, ['avgCostAdjustmentValue']) });
        } else {
            this.setState({ showModalBulkUpdateItems: false, bulkUpdateItemsOperation: '', bulkUpdateItemsValue: undefined });
        }
    }

    public handleShowModalAddSubgroup(e: any, formData: any) {
        if (formData) {
            const formChanges = _.cloneDeep(this.state.formChanges || {});
            _.set(formChanges, 'newSubgroup.groupCode', _.get(formData, 'newSubgroup.groupCode') || _.get(formData, 'newSubgroup.groupCodePlaceholder'));
            this.setState({ formChanges, showModalAddSubgroup: true });
        } else {
            this.setState({ showModalAddSubgroup: true });
        }
    }

    public handleCloseModalAddSubgroup() {
        this.setState({ showModalAddSubgroup: false });
    }
    public handleCloseModalRenameSubgroup() {
        this.setState({ showModalRenameSubgroup: false });
    }
    public handleCloseModalDeleteSubgroup() {
        this.setState({ showModalDeleteSubgroup: false });
    }
    public handleCloseModalDuplicateSubgroup() {
        this.setState({ showModalDuplicateSubgroup: false, formChanges: _.merge({}, this.state.formChanges, { duplicateSubgroup: { groupCode: '' } }) });
    }

    public handleSaveNewSubgroupButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');

        const changeData = _.merge(
            {},
            { regionalComponentListId },
            { subgroup: _.get(this.state.formChanges, 'newSubgroup') },
        );

        const subgroupNewCode = _.get(this.state.formChanges, 'newSubgroup.groupCode');

        this.props.newRegionalComponentListSubgroup(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                const formChanges = _.omit(this.state.formChanges || {}, 'newSubgroup');
                this.setState({
                    selectedFilters: { subgroup: _.toUpper(subgroupNewCode || '') },
                    formChanges,
                    showModalAddSubgroup: false,
                });
                this.props.forceRequest();
            }
        });
    }

    public handleRenameSubgroupButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');

        const changeData = _.merge(
            {},
            { regionalComponentListId },
            { subgroup: _.get(this.state.formChanges, 'updateSubgroup') },
        );

        const subgroupUpdateCode = _.get(this.state.formChanges, 'updateSubgroup.groupCode');
        const subgroupRenameCode = _.get(this.state.formChanges, 'updateSubgroup.newGroupCode');

        this.props.updateRegionalComponentListSubgroup(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                const formChanges = _.omit(this.state.formChanges || {}, 'updateSubgroup');
                const sg = subgroupRenameCode || subgroupUpdateCode;
                this.setState({
                    selectedFilters: { subgroup: _.toUpper(sg || '') },
                    formChanges,
                    showModalRenameSubgroup: false,
                });
                this.props.forceRequest();
            }
        });
    }

    public handleDeleteSubgroupButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');

        const changeData = _.merge(
            {},
            { regionalComponentListId },
            { subgroup: _.get(this.state.formChanges, 'deleteSubgroup') },
        );

        this.props.deleteRegionalComponentListSubgroup(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                const formChanges = _.omit(this.state.formChanges || {}, 'deleteSubgroup');
                this.setState({
                    selectedFilters: { subgroup: this.DefaultSubgroupFilter },
                    formChanges,
                    showModalDeleteSubgroup: false,
                });
                this.props.forceRequest();
            }
        });
    }

    public handleDuplicateSubgroupButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');

        const changeData = _.merge(
            {},
            { regionalComponentListId },
            { subgroup: _.pick(_.get(this.state.formChanges, 'duplicateSubgroup'), ['groupCode']) },
        );

        this.props.duplicateRegionalComponentListSubgroup(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                const duplicateToSubgroupCode = Formatters.getStr(response.body, 'newSubgroup.document.groupCode') || Formatters.getStr(response.body, 'newSubgroup.groupCode');
                this.setState({
                    selectedFilters: { subgroup: _.toUpper(duplicateToSubgroupCode || '') },
                    formChanges: _.omit(this.state.formChanges || {}, 'duplicateSubgroup'),
                    showModalDuplicateSubgroup: false,
                });
                this.props.forceRequest();
            }
        });
    }

    public handleConfirmBulkUpdateItemsButton(e: any, updateField: string, updateValue: any, updateAmount?: number) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        const inv = _.map(_.get(this.state, 'selectedKeys', []), (o) => {
            const result: any = { _id: o, bulkUpdate: true, updateAmount: updateAmount ? updateAmount : 0 };
            _.set(result, updateField, updateValue);
            return result;
        });

        const changeData = _.merge(
            {},
            { regionalComponentListId },
            { inventory: inv },
        );

        this.props.updateRegionalComponentListObj(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                if (this.kendoGridWidgetRef) { this.kendoGridWidgetRef.clearSelection(); }
                this.handleCloseModalBulkUpdateItems(true);
                this.props.forceRequest();
            }
        });
    }

    /** Extend regional list items to match inventory grid shape (componentId, componentName, etc.) */
    public extendInventoryDataForGrid(records: Array<any>, invSubgroups: Array<any>) {
        if (!records) { return []; }

        return records.map(r => {
            let record = _.cloneDeep(r);

            record.categoryName = record.component ? Formatters.getStr(record, 'component.category', '') : '';

            const compCustomName = Formatters.getStr(record, 'componentName');
            const compNormalName = Formatters.getStr(record, 'component.name');
            record.componentName = compCustomName || compNormalName;

            record.avgCost = 0;
            record.avgCost_sort = 0.0;
            if (AppUtilities.isNumeric(record.bestCost) && AppUtilities.isNumeric(record.worstCost)) {
                const bestCost = Formatters.getFloat(record, 'bestCost');
                const worstCost = Formatters.getFloat(record, 'worstCost');
                const totalCost = Number(bestCost + worstCost);
                if (totalCost !== 0) {
                    record.avgCost = Number(totalCost / 2.0).toFixed(2);
                    record.avgCost_sort = Number(record.avgCost);
                }
            }
            record.avgCost = Formatters.formatCurrencyIntl(record.avgCost, 2);

            const recSubgroup = Formatters.getStr(record, 'subgroup', '');
            if (_.size(invSubgroups) >= 1) {
                const subgroupDef: any = _.find(invSubgroups, ['groupCode', recSubgroup]);
                record.subgroupLabel = recSubgroup;
                if (subgroupDef && Formatters.getStr(subgroupDef, 'title')) {
                    record.subgroupLabel += ` - ${Formatters.getStr(subgroupDef, 'title')}`;
                }
            }

            if (!AppUtilities.isNumeric(record.remainingUsefulLife)) {
                record.remainingUsefulLife = 0;
            }
            record.usefulLife_sort = 0;
            record.remainingUsefulLife_sort = 0;
            if (AppUtilities.isNumeric(record.usefulLife)) { record.usefulLife_sort = parseInt(record.usefulLife, 10); }
            if (AppUtilities.isNumeric(record.remainingUsefulLife)) { record.remainingUsefulLife_sort = parseInt(record.remainingUsefulLife, 10); }

            record.unfunded = false;
            if (
                Formatters.getStr(record, 'usefulLife') === AppDataConstants.UsefulLifeOptions.NotApplicable ||
                Formatters.getStr(record, 'usefulLife') === ''
            ) {
                record.unfunded = true;
            }

            record.complete_yesNo = Formatters.getBool(record, 'complete') ? this.appText('complete-yes') : this.appText('complete-no');
            record.reviewed_yesNo = Formatters.getBool(record, 'reviewed') ? this.appText('reviewed-yes') : this.appText('reviewed-no');

            record.viewInventoryDetailLink = this.appText('view-inventory-detail-link-title');
            record.viewInventoryDetailLinkHref = NavigationLinks.regionalComponentListDetail(
                Formatters.getStr(this.props, 'regionalComponentListId'),
                Formatters.getStr(record, '_id')
            );

            return record;
        });
    }

    public getFormData(regionalComponentList: any, lookups: any) {
        const formChanges = this.state.formChanges || {};

        const regions = _.get(lookups, LK.LookupKeys.Regions, []) || [];
        let regionOptions: Array<{ value: string; text: string }>;
        if (this._cachedRegionOptions && this._cachedRegionOptions.regions === regions) {
            regionOptions = this._cachedRegionOptions.options;
        } else {
            regionOptions = (_.size(regions))
                ? regions.map((r) => {
                    const val = Formatters.getStr(r, 'regionId') || Formatters.getStr(r, '_id') || '';
                    return { value: String(val), text: Formatters.getStr(r, 'name') || '' };
                })
                : [];
            this._cachedRegionOptions = { regions, options: regionOptions };
        }

        const isNew = Formatters.getStr(this.props, 'regionalComponentListId') === 'new';
        const list = regionalComponentList || {};

        const regionObj = _.get(list, 'region');
        const currentRegionId = isNew ? '' : (
            String(
                Formatters.getStr(list, 'region.regionId') ||
                Formatters.getStr(list, 'region._id') ||
                Formatters.getStr(list, 'region.document._id') ||
                Formatters.getStr(list, 'region.document.regionId') ||
                Formatters.getStr(list, 'regionId') ||
                (regionObj && typeof regionObj === 'string' ? regionObj : '') ||
                (regionObj && _.isObject(regionObj) ? (Formatters.getStr(regionObj, '_id') || Formatters.getStr(regionObj, 'regionId')) : '') ||
                ''
            )
        );
        const currentRegionName = Formatters.getStr(list, 'region.name') || Formatters.getStr(regionObj, 'name') || Formatters.getStr(list, 'region.document.name') || '';

        if (currentRegionId && currentRegionName && !regionOptions.some((o) => String(o.value) === String(currentRegionId))) {
            regionOptions = [{ value: String(currentRegionId), text: currentRegionName }, ...regionOptions];
        }
        const currentListType = isNew ? '' : (Formatters.getStr(list, 'listType') || Formatters.getStr(list, 'list_type') || Formatters.getStr(list, 'name', '') || '');
        const currentName = isNew ? '' : (Formatters.getStr(list, 'name') || '');
        const currentDescription = isNew ? '' : (Formatters.getStr(list, 'description') || '');

        return {
            regionId: String(Formatters.getStr(formChanges, 'regionId', currentRegionId) || ''),
            listType: String(Formatters.getStr(formChanges, 'listType', currentListType) || ''),

            name: String(Formatters.getStr(formChanges, 'name', currentName) || ''),
            description: String(Formatters.getStr(formChanges, 'description', currentDescription) || ''),
            options: {
                regionOptions: regionOptions || [],
            },
        };
    }

    public handleSaveFormFields(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        const formData = this.getFormData(this.props.regionalComponentList, this.props.lookups);
        const isNew = regionalComponentListId === 'new';

        if (isNew) {
            // Create new list
            if (!formData.regionId || !formData.name) {
                // Validation error - region and name are required
                return;
            }

            const changeData = {
                regionId: formData.regionId,
                name: formData.name,
                description: formData.description,
                listType: formData.listType,
            };

            this.props.newRegionalComponentList(changeData, token, false).then(response => {
                if (response.status >= 200 && response.status < 300) {
                    const newListId = Formatters.getStr(response.body, 'regionalComponentList.document._id');
                    if (newListId) {
                        this.props.navigateTo(NavigationLinks.regionalComponentListEdit(newListId));
                    }
                }
            });
        } else {
            // Update existing list
            const changeData = _.merge(
                {},
                { regionalComponentListId: regionalComponentListId },
                { regionId: formData.regionId },
                { listType: formData.listType },
            );

            this.props.updateRegionalComponentListObj(changeData, token, false).then(response => {
                if (response.status >= 200 && response.status < 300) {
                    this.setState({
                        formChanges: {},
                    });
                    this.originalFormDataCache = {};
                    this.props.forceRequest();
                }
            });
        }
    }

    public getNextLetter(usedCodes: Array<string> = []) {
        const used = _.uniq(_.map(usedCodes, (c) => _.toUpper(c)));
        const singleLetters = _.map(_.range(65, 91), (c) => String.fromCharCode(c));
        const doubleLetters = _.map(singleLetters, (c) => c + c);
        const allCodes = _.concat(singleLetters, doubleLetters);
        const unusedCodes = _.difference(allCodes, used);
        return _.first(unusedCodes);
    }

    public buildOptions(lookups: any, key: any, srcValueField: any = 'name', srcTextField: any = 'name') {
        const l = _.get(lookups, key, []);
        if (_.size(l) <= 0) { return []; }
        return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getStr(c, srcValueField), text: Formatters.getStr(c, srcTextField) })), 'value');
    }

    public buildOptionsIntValue(lookups: any, key: any, srcValueField: any = 'name', srcTextField: any = 'name', prependValue: boolean = false) {
        const l = _.get(lookups, key, []);
        if (_.size(l) <= 0) { return []; }
        if (prependValue) {
            return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getInt(c, srcValueField), text: `${Formatters.getInt(c, srcValueField)} - ${Formatters.getStr(c, srcTextField)}` })), 'value');
        }
        return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getInt(c, srcValueField), text: Formatters.getStr(c, srcTextField) })), 'value');
    }

    /** Inventory-style formData for grid/toolbar/modals (template = regionalTemplateAssociation, order = {} for regional). */
    public getFormDataForGrid(template: any, currentOrder: any, inventoryTransformed: any, orderStatuses: any, subgroupTemplates: any, userInventoryTemplates: any, lookups: any) {
        const orderStatusOptions = (_.size(orderStatuses)) ? orderStatuses.map((c) => _.merge({}, { value: Formatters.getInt(c, 'statusId'), text: Formatters.getStr(c, 'name') })) : [];
        const newSubgroupTemplateOptions = (_.size(subgroupTemplates)) ? subgroupTemplates.map((c) => _.merge({}, { value: Formatters.getInt(c, 'templateId'), text: Formatters.getStr(c, 'name') })) : [];
        const templateSubgroups = _.get(template, 'templateSubgroups', []) || [];
        const templateSubgroupsSorted = _.sortBy(templateSubgroups, (inv) => _.isFinite(_.parseInt(inv.groupCode)) ? _.parseInt(inv.groupCode) : inv.groupCode);
        const subgroupsFiltered = _.reject(templateSubgroupsSorted, (each) => each.groupCode === '');
        const subgroupOptions = (_.size(subgroupsFiltered) >= 1) ? subgroupsFiltered.map((c) => _.merge({}, { value: Formatters.getStr(c, 'groupCode'), text: Formatters.composeInventorySubgroupLabel(c, true) })) : [];
        const inventorySubgroupsWithAny = _.size(templateSubgroupsSorted) >= 1
            ? [{ groupCode: '', title: '', label: this.appText('filter-option-any') }, ...templateSubgroupsSorted.map((c) => (c.label ? c : { ...c, label: Formatters.composeInventorySubgroupLabel(c, true) }))]
            : templateSubgroupsSorted;
        const hasPrevPictures = _.some((inventoryTransformed || []).map((item) => _.isEmpty(_.get(item, 'pictures')) && !_.isEmpty(_.get(item, 'prevPictures', []))));
        const inventoryTemplateUserOptions = (_.size(_.get(lookups, 'users', [])) >= 1) ? (lookups.users || []).map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.composeUsernameLabel(c, true) })) : [];
        const userInventoryTemplateOptions = (_.size(userInventoryTemplates) >= 1) ? userInventoryTemplates.map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.getStr(c, 'name') })) : [];
        const contactNotes = this.transformNotes(_.get(currentOrder, 'contact.notes', []));
        const auth = new Auth();
        const authUserObjId = auth.getSessionUserObjectId();
        const userObjIdDisabled = (auth.isAdmin()) ? false : true;
        const result = {
            clientNotes: this.transformNotes(_.get(currentOrder, 'client.notes', [])),
            contactNotes,
            inventory: inventoryTransformed,
            inventorySubgroups: inventorySubgroupsWithAny,
            adjustRULYears: 0,
            clearPicturesQuestions: false,
            restorePrevPictures: false,
            hasPrevPictures: hasPrevPictures,
            componentFilterHideUnfunded: false,
            avgCostAdjustmentValue: 0,
            moveItems: { toGroupCode: '' },
            newSubgroup: { groupCode: '', groupCodePlaceholder: '', title: '', template: '', templateOptions: newSubgroupTemplateOptions },
            updateSubgroup: { groupCode: '', newGroupCode: '', title: '' },
            deleteSubgroup: { groupCode: '' },
            duplicateSubgroup: { groupCode: '' },
            addInventoryTemplate: { groupCode: '', name: '', description: '', includeCostsCheckbox: false, userObjId: authUserObjId, userObjIdDisabled },
            applyInventoryTemplate: { _id: '', templateObjIdDisabled: false },
            options: { subgroups: subgroupOptions || [], inventoryTemplateUserOptions, userInventoryTemplateOptions },
            currentOrder: currentOrder,
            orderStatusId: _.get(currentOrder, 'status.statusId', 0),
            orderStatuses,
            orderStatusOptions,
        };
        const usedCodes = (_.size(result.inventorySubgroups)) ? _.uniq(_.map(result.inventorySubgroups, (o) => o.groupCode)).filter((c) => c && c !== '') : [];
        const next = this.getNextLetter(usedCodes);
        _.set(result, 'newSubgroup.groupCodePlaceholder', next);
        _.set(result, 'newSubgroup.groupCode', next);
        let merged = _.merge(result, this.state.formChanges);
        _.set(merged, 'inventory', this.transformInventoryRUL(merged.inventory, merged.adjustRULYears, merged.componentFilterHideUnfunded, merged.clearPicturesQuestions, merged.restorePrevPictures));
        return merged;
    }

    // Renderers
    public renderFormSection() {
        const { regionalComponentList, lookups } = this.props;
        const isNew = Formatters.getStr(this.props, 'regionalComponentListId') === 'new';

        const formData = this.getFormData(regionalComponentList, lookups);

        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const inputPropsRegionOnly = { ...defaultInputProps, className: 'inventory-detail-input-full-width' };
        const inputPropsDropdown = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });
        const inputPropsText = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        return (
            <DetailFormCommon.InventoryDetailFormSection title="">

                <KendoWrappers.KendoDropdownListInput
                    name="regionId"
                    clearable={false}
                    label="Region"
                    valuePrimitive={true}
                    value={formData.regionId || ''}
                    data={formData.options.regionOptions}
                    textField="text"
                    valueField="value"
                    onChange={(o) => {
                        const newVal = _.get(o, 'target.value');
                        if (String(newVal) === String(formData.regionId || '')) { return; }
                        this.handleFormInputChange({ target: { name: 'regionId', type: 'kendo-dropdownlist', value: newVal } }, o);
                    }}
                    {...inputPropsRegionOnly}
                />

                {isNew && (
                    <KendoWrappers.KendoTextInputDebounced
                        name="name"
                        label="Name"
                        value={formData.name}
                        minLength={1}
                        {...inputPropsText}
                    />
                )}

                <KendoWrappers.KendoTextInputDebounced
                    name="listType"
                    label="List Type"
                    value={formData.listType}
                    minLength={1}
                    {...inputPropsText}
                />

                <KendoWrappers.KendoTextareaDebounced
                    name="description"
                    label="Description"
                    value={formData.description}
                    rows={3}
                    {...inputPropsText}
                />
            </DetailFormCommon.InventoryDetailFormSection>
        );
    }

    public renderModalRemoveTemplateItem() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalRemoveTemplateItem} onHide={() => this.handleCloseModalRemoveTemplateItem()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-template-item.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete-template-item.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmRemoveTemplateItem(e); }}>{this.appText('modals.delete-template-item.delete')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalRemoveTemplateItem()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalRenameTemplate() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalRenameTemplate} onHide={() => this.handleCloseModalRenameTemplate()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.rename-template.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.rename-template.body')}
                    <KendoWrappers.KendoTextInputDebounced
                        name="renameTemplate.newName"
                        label={this.appText('modals.rename-template-new-name')}
                        placeholder={this.appText('modals.rename-template-new-name.placeholder')}
                        value={Formatters.getStr(this.state.editData, 'newName')}
                        required={false}
                        handleChange={(e) => { this.setState({ editData: _.merge({}, this.state.editData, { newName: e.target.value }) }); }}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="renameTemplate.newDescription"
                        label={this.appText('modals.rename-template-new-name-description')}
                        placeholder={this.appText('modals.rename-template-new-name-description.placeholder')}
                        value={Formatters.getStr(this.state.editData, 'newDescription')}
                        required={false}
                        handleChange={(e) => { this.setState({ editData: _.merge({}, this.state.editData, { newDescription: e.target.value }) }); }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmRenameTemplate(e); }}>{this.appText('modals.rename-template.rename')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalRenameTemplate()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalDeleteTemplate() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalDeleteTemplate} onHide={() => this.handleCloseModalDeleteTemplate()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-template.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete-template.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmDeleteTemplate(e); }}>{this.appText('modals.delete-template.delete')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalDeleteTemplate()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderDeleteItemsModal = () => (
        <Modal enforceFocus={false} show={this.state.showModalDeleteItems} onHide={() => this.handleCloseModalDeleteItems()}>
            <Modal.Header closeButton={true}>
                <Modal.Title>{this.appText('modals.delete-items.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>{this.appText('modals.delete-items.body') || 'Permanently delete all selected items?'}</div>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle="success" onClick={(e) => this.handleConfirmDeleteItemsButton(e)}>{this.appText('links.delete-items')}</Button>
                <Button bsStyle="danger" onClick={() => this.handleCloseModalDeleteItems()}>{this.appTextCommon('buttons.cancel')}</Button>
            </Modal.Footer>
        </Modal>
    )

    public renderMoveItemsModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const passEvent = (e: any) => { this.handleFormInputChange(e, e); };
        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, { handleChange: passEvent, className: 'inventory-detail-input-full-width' });
        const toGroupCodeRaw = _.get(this.state, 'formChanges.moveItems.toGroupCode');
        const toGroupCode = _.isObject(toGroupCodeRaw) ? _.get(toGroupCodeRaw, 'value') : toGroupCodeRaw;
        const disabled = !toGroupCode || String(toGroupCode).trim() === '';
        const subgroupOptions = _.get(formData, 'options.subgroups', []);

        return (
            <Modal enforceFocus={false} show={this.state.showModalMoveItems} onHide={() => this.handleCloseModalMoveItems()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.move-items.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <DetailFormCommon.InventoryDetailFormSection title="">
                        <KendoWrappers.KendoDropdownListInput
                            name="moveItems.toGroupCode"
                            clearable={true}
                            valuePrimitive={true}
                            label={this.appText('modals.move-items.to-subgroup')}
                            value={toGroupCode != null ? toGroupCode : ''}
                            data={subgroupOptions}
                            textField="text"
                            valueField="value"
                            onChange={(o) => {
                                const v = _.get(o, 'target.value') != null ? _.get(o, 'target.value') : _.get(o, 'value');
                                this.setState({
                                    formChanges: _.merge({}, this.state.formChanges, { moveItems: { toGroupCode: v } }),
                                });
                            }}
                            {...inputPropsVirtualizedSelect}
                        />
                    </DetailFormCommon.InventoryDetailFormSection>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={disabled} onClick={(e) => this.handleConfirmMoveItemsButton(e)}>{this.appText('links.move-items')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalMoveItems()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderBulkUpdateItemsModal = (formData: any) => {
        const bulkOperation = Formatters.getStr(this.state, 'bulkUpdateItemsOperation');
        if (!bulkOperation) { return (<div />); }

        let opTokenText = '';
        let opFieldName = '';
        let opFieldValue: any;
        let opFieldAmount: number | undefined;
        const hasValue = bulkOperation === ItemBulkUpdateOperations.SetItemsModifyAvgCost;

        switch (bulkOperation) {
            case ItemBulkUpdateOperations.SetItemsComplete:
                opTokenText = this.appText('modals.bulk-update-items.generic.complete');
                opFieldName = ItemBulkUpdateFields.InventoryComplete;
                opFieldValue = true;
                break;
            case ItemBulkUpdateOperations.SetItemsIncomplete:
                opTokenText = this.appText('modals.bulk-update-items.generic.incomplete');
                opFieldName = ItemBulkUpdateFields.InventoryComplete;
                opFieldValue = false;
                break;
            case ItemBulkUpdateOperations.SetItemsReviewed:
                opTokenText = this.appText('modals.bulk-update-items.generic.reviewed');
                opFieldName = ItemBulkUpdateFields.InventoryReviewed;
                opFieldValue = true;
                break;
            case ItemBulkUpdateOperations.SetItemsNeedReview:
                opTokenText = this.appText('modals.bulk-update-items.generic.needs-review');
                opFieldName = ItemBulkUpdateFields.InventoryReviewed;
                opFieldValue = false;
                break;
            case ItemBulkUpdateOperations.SetItemsModifyAvgCost:
                opTokenText = this.appText('modals.bulk-update-items.generic.modify-avg-cost');
                opFieldName = ItemBulkUpdateFields.InventoryModified;
                opFieldValue = true;
                opFieldAmount = Formatters.getFloat(this.state, 'formChanges.avgCostAdjustmentValue');
                break;
            default:
                return (<div />);
        }

        const modalButtonHandler = (e: any) => (hasValue ? this.handleConfirmBulkUpdateItemsButton(e, opFieldName, opFieldValue, opFieldAmount) : this.handleConfirmBulkUpdateItemsButton(e, opFieldName, opFieldValue));
        const inputPropsVirtualizedSelect = { colLabelXs: 4, colInputXs: 8, className: 'inventory-detail-input-full-width' };
        const modalValueEntry = hasValue ? (
            <div>
                <KendoWrappers.KendoPercentageInput
                    name="bulk-modify-avg-cost"
                    label={this.appText('modals.modify-avg-costs.label')}
                    value={Formatters.getFloat(this.state, 'formChanges.avgCostAdjustmentValue')}
                    step={0.01}
                    onChange={(o) => this.setState({ formChanges: _.merge({}, this.state.formChanges, { avgCostAdjustmentValue: _.get(o, 'target.value') }) })}
                    {...inputPropsVirtualizedSelect}
                />
            </div>
        ) : null;

        return (
            <Modal enforceFocus={false} show={this.state.showModalBulkUpdateItems} onHide={() => this.handleCloseModalBulkUpdateItems(false)}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.bulk-update-items.generic.title', true, opTokenText)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>{this.appText('modals.bulk-update-items.generic.note', true, opTokenText)}</div>
                    {modalValueEntry}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => modalButtonHandler(e)}>{this.appText('modals.bulk-update-items.generic.update-items', true, opTokenText)}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalBulkUpdateItems(false)}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderAddSubgroupModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const passEvent = (e: any) => { this.handleFormInputChange(e, e); };
        const inputProps = _.merge({}, defaultInputProps, { onChange: passEvent, handleChange: passEvent });
        const inputPropsFullWidth = _.merge({}, defaultInputProps, { onChange: passEvent, handleChange: passEvent, className: 'inventory-detail-input-full-width' });
        const disabled = (Formatters.getStr(formData, 'newSubgroup.groupCode') === '') || (Formatters.getStr(formData, 'newSubgroup.title') === '');
        return (
            <Modal show={this.state.showModalAddSubgroup} onHide={() => this.handleCloseModalAddSubgroup()}>
                <Modal.Header closeButton={true}><Modal.Title>{this.appText('modals.add-subgroup.title')}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <DetailFormCommon.InventoryDetailFormSection title="">
                        <KendoWrappers.KendoTextInputDebounced
                            name="newSubgroup.groupCode"
                            label={this.appText('new-subgroup-code')}
                            value={_.get(formData, 'newSubgroup.groupCode')}
                            minLength={1}
                            maxLength={2}
                            placeholder={`Next code: ${_.get(formData, 'newSubgroup.groupCodePlaceholder', '')}`}
                            {...inputProps}
                        />
                        <KendoWrappers.KendoTextInputDebounced name="newSubgroup.title" label={this.appText('new-subgroup-title')} value={_.get(formData, 'newSubgroup.title')} minLength={1} {...inputPropsFullWidth} />
                    </DetailFormCommon.InventoryDetailFormSection>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={disabled} onClick={(e) => this.handleSaveNewSubgroupButton(e)}>{this.appText('add-subgroup')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalAddSubgroup()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderRenameSubgroupModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const passEvent = (e: any) => { this.handleFormInputChange(e, e); };
        const inputPropsFullWidth = _.merge({}, defaultInputProps, { onChange: passEvent, handleChange: passEvent, className: 'inventory-detail-input-full-width' });
        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, { className: 'inventory-detail-input-full-width' });
        const disabled = (Formatters.getStr(formData, 'updateSubgroup.groupCode') === '') || ((Formatters.getStr(formData, 'updateSubgroup.title') === '') && (Formatters.getStr(formData, 'updateSubgroup.newGroupCode') === ''));
        return (
            <Modal show={this.state.showModalRenameSubgroup} onHide={() => this.handleCloseModalRenameSubgroup()}>
                <Modal.Header closeButton={true}><Modal.Title>{this.appText('modals.rename-subgroup.title')}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <DetailFormCommon.InventoryDetailFormSection title="">
                        <KendoWrappers.KendoDropdownListInput
                            name="updateSubgroup.groupCode"
                            clearable={true}
                            valuePrimitive={true}
                            label={this.appText('subgroup')}
                            value={_.get(formData, 'updateSubgroup.groupCode')}
                            data={_.get(formData, 'options.subgroups', [])}
                            textField="text"
                            valueField="value"
                            onChange={(o) => {
                                const v = _.get(o, 'target.value') != null ? _.get(o, 'target.value') : _.get(o, 'value');
                                this.handleFormInputChange({ target: { name: 'updateSubgroup.groupCode', type: 'kendo-dropdownlist', value: v } }, o);
                            }}
                            {...inputPropsVirtualizedSelect}
                        />
                        <KendoWrappers.KendoTextInputDebounced
                            name="updateSubgroup.newGroupCode"
                            label={this.appText('rename-subgroup-code')}
                            value={_.get(formData, 'updateSubgroup.newGroupCode')}
                            placeholder={_.get(formData, 'updateSubgroup.groupCode')}
                            minLength={1}
                            maxLength={2}
                            {...inputPropsFullWidth}
                        />
                        <KendoWrappers.KendoTextInputDebounced name="updateSubgroup.title" label={this.appText('rename-subgroup-title')} value={_.get(formData, 'updateSubgroup.title')} minLength={1} {...inputPropsFullWidth} />
                    </DetailFormCommon.InventoryDetailFormSection>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={disabled} onClick={(e) => this.handleRenameSubgroupButton(e)}>{this.appText('rename-subgroup')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalRenameSubgroup()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderDeleteSubgroupModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, { className: 'inventory-detail-input-full-width' });
        const disabled = (Formatters.getStr(formData, 'deleteSubgroup.groupCode') === '');
        return (
            <Modal show={this.state.showModalDeleteSubgroup} onHide={() => this.handleCloseModalDeleteSubgroup()}>
                <Modal.Header closeButton={true}><Modal.Title>{this.appText('modals.delete-subgroup.title')}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <DetailFormCommon.InventoryDetailFormSection title="">
                        <KendoWrappers.KendoDropdownListInput
                            name="deleteSubgroup.groupCode"
                            clearable={true}
                            label={this.appText('new-subgroup-code')}
                            value={_.get(formData, 'deleteSubgroup.groupCode')}
                            data={_.get(formData, 'options.subgroups', [])}
                            textField="text"
                            valueField="value"
                            onChange={(o) => {
                                this.handleFormInputChange(
                                    { target: { name: 'deleteSubgroup.groupCode', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } },
                                    o
                                );
                            }}
                            {...inputPropsVirtualizedSelect}
                        />
                    </DetailFormCommon.InventoryDetailFormSection>
                    <div>{this.appText('modals.delete-subgroup.note') || 'Note: any inventory components in this group will be reassigned to subgroup \'A\''}</div>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={disabled} onClick={(e) => this.handleDeleteSubgroupButton(e)}>{this.appText('delete-subgroup')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalDeleteSubgroup()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderAddComponentModal = () => {
        const components = _.get(this.props, 'lookups.' + LK.LookupKeys.Components, []) as any[];
        const componentOptions = (_.size(components) >= 1)
            ? _.orderBy(
                components.map((c) => ({
                    value: String(Formatters.getStr(c, 'componentId') || Formatters.getStr(c, '_id')),
                    text: `${Formatters.getStr(c, 'componentId') || Formatters.getStr(c, '_id')} - ${Formatters.getStr(c, 'name', '')}`,
                })),
                'text'
            )
            : [];
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, { className: 'inventory-detail-input-full-width' });
        const disabled = !this.state.addComponentSelectedId;
        return (
            <Modal show={!!this.state.showModalAddComponent} onHide={() => this.handleCloseModalAddComponent()}>
                <Modal.Header closeButton={true}><Modal.Title>{this.appText('add-component')}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <DetailFormCommon.InventoryDetailFormSection title="">
                        <KendoWrappers.KendoDropdownListInput
                            name="addComponent.componentId"
                            clearable={true}
                            label={this.appText('component')}
                            value={this.state.addComponentSelectedId}
                            data={componentOptions}
                            textField="text"
                            valueField="value"
                            onChange={(o) => { this.setState({ addComponentSelectedId: _.get(o, 'target.value') || '' }); }}
                            {...inputPropsVirtualizedSelect}
                        />
                    </DetailFormCommon.InventoryDetailFormSection>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={disabled} onClick={() => this.handleConfirmAddComponent()}>{this.appText('add-component')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalAddComponent()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderDuplicateSubgroupModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, { className: 'inventory-detail-input-full-width' });
        const disabled = Formatters.getStr(formData, 'duplicateSubgroup.groupCode') === '';
        return (
            <Modal enforceFocus={false} show={this.state.showModalDuplicateSubgroup} onHide={() => this.handleCloseModalDuplicateSubgroup()}>
                <Modal.Header closeButton={true}><Modal.Title>{this.appText('modals.duplicate-subgroup.title')}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <DetailFormCommon.InventoryDetailFormSection title="">
                        <KendoWrappers.KendoDropdownListInput
                            name="duplicateSubgroup.groupCode"
                            clearable={true}
                            label={this.appText('modals.duplicate-subgroup.subgroup')}
                            value={_.get(formData, 'duplicateSubgroup.groupCode')}
                            data={_.get(formData, 'options.subgroups', [])}
                            textField="text"
                            valueField="value"
                            onChange={(o) => {
                                this.handleFormInputChange(
                                    { target: { name: 'duplicateSubgroup.groupCode', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } },
                                    o
                                );
                            }}
                            {...inputPropsVirtualizedSelect}
                        />
                    </DetailFormCommon.InventoryDetailFormSection>
                    <div>{this.appText('modals.duplicate-subgroup.note')}</div>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={disabled} onClick={(e) => this.handleDuplicateSubgroupButton(e)}>{this.appText('duplicate-subgroup')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalDuplicateSubgroup()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public CurrentOrderSection = ({ formData }: { formData: any }) => {
        if (!formData) { return <div />; }
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const inputProps = _.merge({}, defaultInputProps, { onChange: (n: string, v: any) => this.handleFormInputChange({ target: { name: n, type: 'text', value: v } }, { value: v }) });
        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, { className: 'inventory-detail-input-full-width' });
        const disabled = (Formatters.getInt(formData, 'currentOrder.orderId') === 0) || (Formatters.getInt(formData, 'orderStatusId') === 0);
        return (
            <div>
                <div>
                    <KendoWrappers.KendoDisabledInputHidden name="_currentOrder.orderId" label={this.appText('current-order')} value={`${Formatters.getStr(formData, 'currentOrder.orderId')}`} {...inputProps} />
                </div>
                <div>
                    <KendoWrappers.KendoDropdownListInput
                        name="orderStatusId"
                        disabled={disabled}
                        readOnly={disabled}
                        label={this.appText('status')}
                        value={formData.orderStatusId}
                        data={formData.orderStatusOptions || []}
                        textField="text"
                        valueField="value"
                        onChange={(o) => this.handleFormInputChange({ target: { name: 'orderStatusId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o)}
                        {...inputPropsVirtualizedSelect}
                    />
                </div>
            </div>
        );
    }

    public AdjustRULsSection = ({ formData }: { formData: any }) => {
        if (!formData) { return <div />; }
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };
        const inputPropsAdjust = _.merge({}, defaultInputProps, { colLabelXs: 5, colInputXs: 7, onChange: (n: string, v: any) => this.handleFormInputChange({ target: { name: n, type: 'text', value: v } }, { value: v }) });
        const inputPropsRoleCheckbox = _.merge({}, defaultInputProps, { colLabelXs: 5, colInputXs: 7, onChange: (n: string, v: any) => this.handleFormInputChange({ target: { name: n, type: 'kendo-checkbox', value: v } }, { value: v }) });
        let saveChangesButtonProps = { bsStyle: 'default' as any, bsSize: 'xs' as any, disabled: true, title: this.appTextCommon('buttons.no-changes') };
        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'xs',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')} ${Formatters.plural(this.appTextCommon('buttons.change'), this.getFormChangesCount(), false)}`,
            };
        }
        const restorePrevPictures = _.get(formData, 'hasPrevPictures', false)
            ? <div><KendoWrappers.KendoCheckbox name="restorePrevPictures" label={this.appText('restore-prev-pictures')} value={Formatters.getBool(formData, 'restorePrevPictures')} {...inputPropsRoleCheckbox} /></div>
            : <div />;
        const enableRestore = !_.get(formData, 'hasPrevPictures', false);
        return (
            <div>
                <div><KendoWrappers.KendoNumberInput name="adjustRULYears" label={this.appText('adjust-rul-years')} min={-100} max={100} value={formData.adjustRULYears} {...inputPropsAdjust} /></div>
                <div><KendoWrappers.KendoCheckbox name="clearPicturesQuestions" label={this.appText('clear-pictures-questions')} value={Formatters.getBool(formData, 'clearPicturesQuestions')} {...inputPropsRoleCheckbox} /></div>
                <div><KendoWrappers.KendoCheckbox name="restorePrevPictures" label={this.appText('restore-prev-pictures')} value={Formatters.getBool(formData, 'restorePrevPictures')} disabled={enableRestore} {...inputPropsRoleCheckbox} /></div>
                <div className="regional-component-list-manipulation-footer">
                    <ButtonToolbar>
                        <Button {...saveChangesButtonProps} onClick={(e) => this.handleSaveButton(e)}>
                            <i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}
                        </Button>
                    </ButtonToolbar>
                </div>
            </div>
        );
    }

    public handleSaveButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');
        const regionalComponentListId = Formatters.getStr(this.props, 'regionalComponentListId');
        const isNew = regionalComponentListId === 'new';
        const formData = this.getFormData(this.props.regionalComponentList, this.props.lookups);

        if (isNew) {
            if (!formData.regionId || !formData.name) {
                return;
            }
            const createChangeData = {
                regionId: formData.regionId,
                name: formData.name,
                description: formData.description,
                listType: formData.listType,
            };
            this.props.newRegionalComponentList(createChangeData, token, false).then((response: any) => {
                if (response.status >= 200 && response.status < 300) {
                    const newListId = Formatters.getStr(response.body, 'regionalComponentList.document._id');
                    if (newListId) {
                        this.props.navigateTo(NavigationLinks.regionalComponentListEdit(newListId));
                    }
                }
            });
            return;
        }

        const inventoryOnlyChanges = _.omit(this.state.formChanges, ['newSubgroup', 'clearPicturesQuestions', 'restorePrevPictures', 'regionId', 'name', 'description', 'listType']);
        const changeData = _.merge(
            {},
            { regionalComponentListId },
            { regionId: formData.regionId, listType: formData.listType, name: formData.name, description: formData.description },
            inventoryOnlyChanges
        );
        this.props.updateRegionalComponentListObj(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.setState({ selectedKeys: [], formChanges: {}, showModalSaveChanges: false });
                if (this.kendoGridWidgetRef) { this.kendoGridWidgetRef.clearSelection(); }
            }
        });
    }

    public renderButtonToolbar = (formData: any, disableReport: boolean = false, disableClientQuestions: boolean = false) => {
        let saveChangesButtonProps = {
            bsStyle: 'default' as any,
            bsSize: 'sm' as any,
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };
        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')} ${Formatters.plural(this.appTextCommon('buttons.change'), this.getFormChangesCount(), false)}`,
            };
        }
        const btnStyle = { whiteSpace: 'nowrap' as any };
        return (
            <div>
                <Well className="well-sm">
                    <Row>
                        <Col xs={12}>
                            <ButtonToolbar style={{ flexWrap: 'wrap' }}>
                                <Button {...saveChangesButtonProps} style={btnStyle} onClick={e => { this.handleSaveButton(e); }}>
                                    <i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}
                                </Button>
                                <Button bsSize="sm" bsStyle="info" style={btnStyle} onClick={(e) => { this.handleAddComponentButton(formData); }}>
                                    <i className="glyphicon glyphicon-plus" /> {this.appText('add-component')}
                                </Button>
                                <Button bsSize="sm" bsStyle="info" style={btnStyle} onClick={(e) => this.handleShowModalAddSubgroup(e, formData)}>
                                    <i className="glyphicon glyphicon-plus" /> {this.appText('add-subgroup')}
                                </Button>
                                <Button bsSize="sm" bsStyle="info" style={btnStyle} onClick={e => { this.setState({ showModalRenameSubgroup: true }); }}>
                                    <i className="glyphicon glyphicon-edit" /> {this.appText('rename-subgroup')}
                                </Button>
                                <Button bsSize="sm" bsStyle="info" style={btnStyle} onClick={e => { this.setState({ showModalDuplicateSubgroup: true }); }}>
                                    <i className="glyphicon glyphicon-copy" /> {this.appText('duplicate-subgroup')}
                                </Button>
                                <Button bsSize="sm" bsStyle="info" style={btnStyle} onClick={e => { this.setState({ showModalDeleteSubgroup: true }); }}>
                                    <i className="glyphicon glyphicon-trash" /> {this.appText('delete-subgroup')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public RegionalInventoryComponents = ({ formData }: { formData: any }) => {
        if (!formData) { return <div />; }

        let subgroupFilter = _.get(this.state, 'selectedFilters.subgroup', this.DefaultSubgroupFilter);
        const invTransformedUnsorted = _.get(formData, 'inventory', []);
        const invSubgroups = _.get(formData, 'inventorySubgroups', []);
        const subgroupCount = Math.max(0, _.size(invSubgroups) - 1);
        const subgroupOptions = FormUtilities.buildSubgroupSelectOptions(invSubgroups, 'groupCode', 'label', 'groupCode');

        if (subgroupFilter === this.DefaultSubgroupFilter && _.get(subgroupOptions, '[0].value') !== undefined) {
            subgroupFilter = Formatters.getStr(subgroupOptions, '[0].value');
        }
        const shouldFilter = (subgroupFilter && subgroupFilter !== this.DefaultSubgroupFilter && subgroupFilter !== '') ? true : false;
        let filter: any;
        if (shouldFilter) {
            filter = { field: 'subgroup', operator: 'eq', value: subgroupFilter };
        }

        const invFiltered = invTransformedUnsorted;
        const invTransformedSorted = _.sortBy(invFiltered, (inv) => (_.isFinite(_.parseInt(inv.subgroup)) ? _.parseInt(inv.subgroup) : inv.subgroup));
        const invComponentNames = _.map(invTransformedSorted, (item) => ({ name: item.componentName, item })).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
        const invTransformed = _.map(invComponentNames, (each) => each.item);
        const invIdsWithDecimals = _.map(invTransformed, (each) => {
            const row = { ...each };
            row.componentId = (each.componentDecimal) ? _.toNumber(each.componentId + '.' + each.componentDecimal) : _.toNumber(each.componentId);
            return row;
        });

        const dataSourceInventory = new (window as any).kendo.data.DataSource({
            data: invIdsWithDecimals,
            filter: filter,
            sort: [{ dir: 'asc', field: 'componentId' }],
            group: [{ field: 'subgroupLabel' }],
            schema: { model: { id: '_id' } },
        });
        dataSourceInventory.fetch();

        const gridButtonsDisabled = (_.size(_.get(this.state, 'selectedKeys', [])) > 0) ? false : true;
        const gridButtons = (
            <Row>
                <Col xs={4}>
                    <ButtonToolbar>
                        <Button bsSize="xs" disabled={gridButtonsDisabled} bsStyle="info" onClick={(e) => this.handleShowModalDeleteItems(e)}>
                            <i className="fa fa-trash-o" /> {this.appText('links.delete-items')}
                        </Button>
                        <Button bsSize="xs" disabled={gridButtonsDisabled} bsStyle="info" onClick={(e) => this.handleShowModalMoveItems(e)}>
                            <i className="fa fa-share-square-o" /> {this.appText('links.move-items')}
                        </Button>
                    </ButtonToolbar>
                </Col>
                <Col xs={4}>
                    <ButtonToolbar className="regional-component-list-grid-toolbar" />
                </Col>
                <Col xs={4}>
                    <ButtonToolbar>
                        <Button bsSize="xs" disabled={gridButtonsDisabled} bsStyle="info" onClick={(e) => this.handleShowModalBulkUpdateItems(e, ItemBulkUpdateOperations.SetItemsModifyAvgCost)}>
                            <i className="fa fa-line-chart" /> {this.appText('links.adjust-avg-costs')}
                        </Button>
                    </ButtonToolbar>
                </Col>
            </Row>
        );

        const textComponentsShown = `${dataSourceInventory.view().length} ${this.appText('of')} ${this.appTextPlural('component', _.size(invTransformed), true, true)} ${this.appText('shown')}`;
        return (
            <div>
                <Row>
                    <Col xs={5} xl={4}>
                        <div>
                            <Row>
                                <Col xs={12}>
                                    <h5><b>{this.appText('filter-by-subgroup')}</b></h5>
                                </Col>
                            </Row>
                            <Row className="form-group">
                                <Col xs={6}>
                                    <form onSubmit={e => { e.preventDefault(); }}>
                                        <Select
                                            name="subgroup-filter"
                                            clearable={false}
                                            className="inventory-subgroup-filter-select"
                                            value={subgroupFilter}
                                            options={subgroupOptions}
                                            onChange={e => this.handleSelectSubgroupFilter(e)}
                                        />
                                    </form>
                                </Col>
                                <Col xs={6}>
                                    <div>
                                        <span className="small-link-text">{this.appTextPlural('subgroup', subgroupCount, true, true)} {this.appText('defined')}</span>
                                    </div>
                                    <div>
                                        <span className="small-link-text">{textComponentsShown}</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
                {gridButtons}
                <Row>
                    <Col xs={12}>
                        <Grid dataSource={dataSourceInventory} {...this.gridOptions} />
                    </Col>
                </Row>
            </div>
        );
    }

    public render() {
        const { regionalComponentList: currentRegionalComponentList, lookups, ...rest } = this.props;
        const isNew = Formatters.getStr(this.props, 'regionalComponentListId') === 'new';
        const templateSubgroups: any[] = _.get(currentRegionalComponentList, 'templateSubgroups', []) || [];
        let invItemsFromTemplate: any[] = _.get(currentRegionalComponentList, 'templateInventoryItems');

        const template = this.extendTemplateData(currentRegionalComponentList) || { inventorySubgroups: templateSubgroups };
        const inv = this.extendInventoryDataForGrid(invItemsFromTemplate, templateSubgroups || []);
        const order = {};
        const formData = (isNew || currentRegionalComponentList) ? this.getFormDataForGrid(template, order, inv, [], [], [], lookups) : null;

        if (formData && (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache))) {
            this.originalFormDataCache = _.cloneDeep(formData);
        }

        const hasComponentsWithNegativeRUL = formData && (_.find(inv, (o) => Formatters.getInt(o, 'remainingUsefulLife_sort') < 0));
        const warnCompWithNegativeLife = hasComponentsWithNegativeRUL ? (
            <div className="regional-component-list-alert-negative-rul">
                <Alert bsStyle="warning">
                    <h4><i className="fa fa-exclamation-triangle" /> {this.appText('alerts.warn-component-negative-rul.title')}</h4>
                    <p>{this.appText('alerts.warn-component-negative-rul.body')}</p>
                </Alert>
            </div>
        ) : null;

        const pageTitle = isNew ? 'Create New Regional Component List' : (currentRegionalComponentList ? this.appTextResultsAndPageTitle(_.size(inv)) : 'Regional Component List');

        let page = <div />;

        if (isNew || currentRegionalComponentList) {
            page = (
                <div>
                    {this.renderModalRenameTemplate()}
                    {this.renderModalDeleteTemplate()}
                    {this.renderModalRemoveTemplateItem()}
                    {this.renderDeleteItemsModal()}
                    {formData && this.renderMoveItemsModal(formData)}
                    {formData && this.renderBulkUpdateItemsModal(formData)}
                    {formData && this.renderAddSubgroupModal(formData)}
                    {formData && this.renderRenameSubgroupModal(formData)}
                    {formData && this.renderDeleteSubgroupModal(formData)}
                    {formData && this.renderDuplicateSubgroupModal(formData)}
                    {this.renderAddComponentModal()}
                    {formData && this.renderButtonToolbar(formData, false, false)}
                    <WhiteBoxPageSection title="Regional Component List Details">
                        <Row><Col xs={12} md={6}>{this.renderFormSection()}</Col></Row>
                        <Row>
                            <Col xs={12}>
                                <DetailFormCommon.InventoryDetailFormSection title={this.appText('inventory-components')}>
                                    {warnCompWithNegativeLife}
                                    <this.RegionalInventoryComponents formData={formData} />
                                </DetailFormCommon.InventoryDetailFormSection>
                            </Col>
                        </Row>
                    </WhiteBoxPageSection>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = (objId) => (ApiEndpoints as any).getRegionalComponentListUrl(objId);
const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.Regions,
    LK.LookupKeys.Components,
]);

const mapPropsToConfigs = (props) => {
    const regionalComponentListId = Formatters.getStr(props, 'match.params.regionalComponentListId') || Formatters.getStr(props, 'regionalComponentListId');
    const isNew = regionalComponentListId === 'new';
    
    const configs: any[] = [
        ReduxQueries.Queries.getLookupsByKeyRequest(
            getApiUrlLookups(),
            ReduxQueries.PropsAccessToken(props),
        ),
    ];

    // Only fetch the list if it's not a new list
    if (!isNew) {
        configs.push(
            (ReduxQueries.Queries as any).getRegionalComponentListRequest(
                getApiUrl(regionalComponentListId),
                ReduxQueries.PropsAccessToken(props),
            )
        );
    }

    return configs;
};

const mapStateToProps = (state, props) => {
    const regionalComponentListId = Formatters.getStr(props, 'match.params.regionalComponentListId');
    const isNew = regionalComponentListId === 'new';

    // The update key 'lookups' in getLookupsByKeyRequest means it updates state.entities.lookups
    // MapQueryStateToPropsByUrl already provides lookups from state.entities.lookups by default
    // So we don't need to override it - just use the default
    const baseState = {
        ...ReduxQueries.MapQueryStateToPropsByUrl(getApiUrlLookups(), state, props, {}),
        regionalComponentListId: regionalComponentListId,
    };

    if (!isNew) {
        return {
            ...baseState,
            ...ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(regionalComponentListId), state, props, {
                regionalComponentList: state.entities.regionalComponentListDetail,
            }),
        };
    }

    return {
        ...baseState,
        regionalComponentList: null,
    };
};

const mapDispatchToProps = {
    updateRegionalComponentListObj: (MutateActions as any).updateRegionalComponentListMutateAsync,
    updateRegionalComponentListItem: (MutateActions as any).updateRegionalComponentListItemMutateAsync,
    newRegionalComponentList: (MutateActions as any).newRegionalComponentListMutateAsync,
    newRegionalComponentListItem: (MutateActions as any).newRegionalComponentListItemMutateAsync,
    renameRegionalComponentListObj: (MutateActions as any).renameRegionalComponentListMutateAsync,
    deleteRegionalComponentListObj: (MutateActions as any).deleteRegionalComponentListMutateAsync,
    deleteRegionalComponentListItemObj: (MutateActions as any).deleteRegionalComponentListItemMutateAsync,
    newRegionalComponentListSubgroup: (MutateActions as any).newRegionalComponentListSubgroupMutateAsync,
    updateRegionalComponentListSubgroup: (MutateActions as any).updateRegionalComponentListSubgroupMutateAsync,
    deleteRegionalComponentListSubgroup: (MutateActions as any).deleteRegionalComponentListSubgroupMutateAsync,
    duplicateRegionalComponentListSubgroup: (MutateActions as any).duplicateRegionalComponentListSubgroupMutateAsync,
    navigateTo: (path) => push(path)
};

const RegionalComponentListEditContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(RegionalComponentListEdit);

export default withRouter(RegionalComponentListEditContainer);



// WEBPACK FOOTER //
// ./src/regional-component-lists/edit.tsx